import React from 'react'
import {
  TextField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  ValidationResult,
} from 'react-aria-components'
import {
  CountrySelector,
  ParsedCountry,
  usePhoneInput,
  UsePhoneInputConfig,
} from 'react-international-phone'
import 'react-international-phone/style.css'
import { composeTailwindRenderProps } from '../../utils'
import { AriaButton } from './Button'
import { Description, FieldError, Input, Label } from './Field'

export interface TextFieldProps extends AriaTextFieldProps {
  label?: string
  description?: string
  errorMessage?: string | ((validation: ValidationResult) => string)
  placeholder?: string
  tabIndex?: number
}

export function TextField({
  description,
  errorMessage,
  label,
  onChange,
  placeholder,
  tabIndex,
  type,
  value,
  ...props
}: TextFieldProps) {
  const phoneValues =
    type === 'tel'
      ? usePhoneInput({
          value,
          onChange: ({ phone }) => {
            if (onChange) {
              onChange(phone)
            }
          },
          defaultCountry: 'us',
        } as UsePhoneInputConfig)
      : null

  return (
    <AriaTextField
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        'flex flex-col gap-1'
      )}
    >
      {label && <Label>{label}</Label>}
      {type === 'tel' ? (
        <div className="phone-input flex items-center rounded-md border border-default-300">
          <CountrySelector
            selectedCountry={phoneValues?.country.iso2}
            onSelect={(parsedCountry: ParsedCountry) =>
              phoneValues && phoneValues.setCountry(parsedCountry.iso2)
            }
            renderButtonWrapper={({ children, rootProps }) => (
              <AriaButton
                variant="secondary"
                {...rootProps}
                className="rounded-r-none border-0 border-r"
              >
                {children}
              </AriaButton>
            )}
          />
          <Input
            className="border-0"
            variant="grouped"
            placeholder={placeholder}
            value={phoneValues?.inputValue || ''}
            onChange={phoneValues?.handlePhoneValueChange}
          />
        </div>
      ) : (
        <Input
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange && onChange(e.target.value)}
          tabIndex={tabIndex}
        />
      )}

      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
    </AriaTextField>
  )
}
